// Readability of the homepage banner's white text over its prize photo.
//
// The photo is measured as the banner shows it (cropped to the banner, with
// the banner's own gradient on top), and the result is how much more to
// darken it so the text reaches WCAG AA contrast (4.5:1) over 90% of the
// area behind the text. Used by the banner (HeroScrim) and the builder's
// readability check. Browser only: it needs a canvas and a loaded image.

const TEXT = [0xf3, 0xf7, 0xf4]; // --color-foreground
const SCRIM = [0x09, 0x0b, 0x0a]; // --color-background
export const TARGET_CONTRAST = 4.5;
const MAX_EXTRA = 0.7;

// HomeHero's gradient: [height 0..1, opacity].
const GRADIENT = [
  [0, 0.7],
  [0.26, 0.12],
  [0.48, 0.3],
  [1, 0.97],
];

const lin = (c) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const lum = (r, g, b) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
const TEXT_LUM = lum(...TEXT);
export const contrastWith = (l) => (TEXT_LUM + 0.05) / (l + 0.05);

function gradientAt(y) {
  for (let i = 1; i < GRADIENT.length; i++) {
    const [y1, a1] = GRADIENT[i];
    const [y0, a0] = GRADIENT[i - 1];
    if (y <= y1) return a0 + ((a1 - a0) * (y - y0)) / (y1 - y0);
  }
  return GRADIENT[GRADIENT.length - 1][1];
}

// Browsers blend layers on the stored (sRGB) values, so blend those.
const over = (px, a) => px.map((c, i) => c * (1 - a) + SCRIM[i] * a);

/**
 * { extra, contrast, before }: the extra darkening (0 to 0.7) the banner
 * needs, the contrast with it, and the contrast without it.
 *
 * box: the banner's size in CSS pixels. posX/posY: its object-position.
 * textTop: how far down (0..1) the text starts.
 */
export function measureHero(img, { width, height, posX = 0.5, posY = 0.4, textTop = 0.25 }) {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih || !width || !height) return null;

  // object-fit: cover, in source pixels.
  const scale = Math.max(width / iw, height / ih);
  const sw = width / scale;
  const sh = height / scale;
  const sx = (iw - sw) * posX;
  const sy = (ih - sh) * posY;

  const W = 48;
  const H = Math.max(16, Math.round((W * height) / width));
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data; // throws if the image isn't CORS-clean

  const pixels = [];
  for (let y = Math.floor(textTop * H); y < H; y++) {
    const a = gradientAt((y + 0.5) / H);
    for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      pixels.push(over([data[i], data[i + 1], data[i + 2]], a));
    }
  }

  // The 90th-percentile brightest spot behind the text, with extra darkening.
  const p90 = (extra) => {
    const l = pixels.map((px) => lum(...over(px, extra))).sort((a, b) => a - b);
    return l[Math.floor(0.9 * (l.length - 1))];
  };

  const before = contrastWith(p90(0));
  if (before >= TARGET_CONTRAST) return { extra: 0, contrast: before, before };

  let lo = 0;
  let hi = MAX_EXTRA;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    if (contrastWith(p90(mid)) >= TARGET_CONTRAST) hi = mid;
    else lo = mid;
  }
  return { extra: hi, contrast: contrastWith(p90(hi)), before };
}

/** Loads an image so a canvas may read it (Supabase Storage allows this). */
export function loadForMeasuring(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
